import * as Yup from "yup";

/**
 * Validation and totals for the Tosite form, kept out of the component so the
 * rules can be tested directly rather than through the rendered page.
 */

export type TransactionFormRow = {
  description: string;
  debit: string;
  credit: string;
  accountCode: number | "";
};

/** Empty means zero. Accepts the Finnish decimal comma. */
export const parseAmt = (v: unknown): number => {
  if (v === "" || v == null) return 0;
  const s = String(v).replace(",", ".");
  const n = Number(s);
  return Number.isFinite(n) ? n : NaN;
};

/** Cents, so 0.1 + 0.2 never shows up as a phantom mismatch. */
const toCents = (v: unknown): number => Math.round(parseAmt(v) * 100);

export type VoucherTotals = {
  debit: number;
  credit: number;
  difference: number;
};

/** Running debet/kredit totals for the form's summary line. */
export const computeTotals = (
  transactions: Pick<TransactionFormRow, "debit" | "credit">[]
): VoucherTotals => {
  let debitCents = 0;
  let creditCents = 0;

  for (const tx of transactions ?? []) {
    const d = toCents(tx?.debit);
    const c = toCents(tx?.credit);
    if (Number.isFinite(d)) debitCents += d;
    if (Number.isFinite(c)) creditCents += c;
  }

  return {
    debit: debitCents / 100,
    credit: creditCents / 100,
    difference: Math.abs(debitCents - creditCents) / 100,
  };
};

const MISSING_AMOUNT = "Anna summa joko debet- tai kredit-puolelle";
const BOTH_SIDES =
  "Sama summa on sekä debetissä että kreditissä. Anna summa vain toiselle puolelle — muuten tilin saldo jää nollaksi.";

const amountField = (label: "Debet" | "Kredit") =>
  Yup.string()
    .transform((v) => (v == null ? "" : String(v)))
    .test("is-number", `${label} pitää olla numero`, (v) =>
      v === "" ? true : Number.isFinite(parseAmt(v))
    )
    .test("non-negative", `${label} ei voi olla negatiivinen`, (v) =>
      v === "" ? true : parseAmt(v) >= 0
    );

/**
 * "A line carries one side only", attached to the debet field so it renders.
 *
 * Reading Debet and Kredit as two required fields and typing the amount into
 * both is the single most damaging mistake this form allows, precisely because
 * it looks correct: the voucher balances, nothing warns, and the account nets
 * to zero. 49 production vouchers are like this, and one paying association
 * booked 91 of them — every expense account at exactly 0.00 and an income
 * statement showing no expenses at all.
 */
const withBothSidesCheck = (field: Yup.StringSchema<string | undefined>) =>
  field.test("both-sides", BOTH_SIDES, function (v) {
    const own = parseAmt(v);
    const other = parseAmt(this.parent?.credit);
    if (!Number.isFinite(own) || !Number.isFinite(other)) return true;
    return !(own > 0 && other > 0);
  });

/**
 * "At least one side must carry an amount", attached to a *field* rather than
 * the row object. An object-level test reports at path `transactions[0]`, which
 * Formik stores as a string; the form reads `errors.transactions[i].debit`, so
 * `.debit` on a string is undefined and the message never renders — submit just
 * silently does nothing. Keeping it on the field gives it a path the form
 * displays. Uses `function` (not an arrow) for `this.parent` sibling access.
 */
const withMissingAmountCheck = (
  field: Yup.StringSchema<string | undefined>,
  sibling: "debit" | "credit"
) =>
  field.test("one-sided-amount", MISSING_AMOUNT, function (v) {
    const own = parseAmt(v);
    const other = parseAmt(this.parent?.[sibling]);
    // Defer to is-number / non-negative so their specific message wins.
    if (!Number.isFinite(own) || !Number.isFinite(other)) return true;
    if (own < 0 || other < 0) return true;
    return own > 0 || other > 0;
  });

/**
 * True when every row's amounts are usable numbers.
 *
 * The voucher-level rules below defer while this is false, so the specific
 * row message ("Debet ei voi olla negatiivinen") wins over the general one
 * ("Debet ja kredit eivät täsmää"). Same reasoning as withMissingAmountCheck.
 */
const rowAmountsUsable = (rows: TransactionFormRow[]) =>
  rows.every((tx) => {
    const d = parseAmt(tx?.debit);
    const c = parseAmt(tx?.credit);
    return Number.isFinite(d) && Number.isFinite(c) && d >= 0 && c >= 0;
  });

export const makeValidationSchema = (accountCodes: number[]) => {
  const txRowSchema = Yup.object({
    description: Yup.string()
      .max(120, "Max 120 merkkiä")
      .required("Anna kuvaus"),
    accountCode: Yup.mixed()
      .transform((v) => (v === "" || v == null ? null : Number(v)))
      .oneOf(accountCodes, "Valitse tili")
      .required("Valitse tili"),
    // Neither side is required: leaving the unused one blank is the normal way
    // to book a row, and it reads as zero. The missing-amount check below is
    // what stops an empty row getting through.
    debit: withBothSidesCheck(
      withMissingAmountCheck(amountField("Debet"), "credit")
    ),
    credit: withMissingAmountCheck(amountField("Kredit"), "debit"),
  });

  return Yup.object({
    description: Yup.string()
      .required("Nimi on pakollinen")
      .max(160, "Max 160 merkkiä"),
    date: Yup.date()
      .typeError("Valitse kelvollinen pvm")
      .required("Pvm on pakollinen"),
    transactions: Yup.array()
      .of(txRowSchema)
      .min(1, "Lisää vähintään yksi tapahtuma")
      /**
       * Debet must equal kredit.
       *
       * This used to be deliberately unenforced, on the reasoning that nothing
       * had enforced it before and blocking would stop vouchers that saved
       * today. What actually saved was 138 unbalanced vouchers across 17
       * associations — every one hand-entered — while the in-app help told
       * users the system checked this. It now does.
       * (Production, measured 2.9.2026.)
       *
       * Compared in cents so 0.1 + 0.2 is not a phantom mismatch. The visible
       * message comes from the summary Alert in Voucher.tsx, which is driven by
       * computeTotals rather than Formik: an error reported at an array path is
       * stored by Formik as a string, so a message attached here would block
       * submit without ever rendering.
       */
      .test("balanced", "Debet ja kredit eivät täsmää", (transactions) => {
        const rows = (transactions ?? []) as TransactionFormRow[];
        if (!rowAmountsUsable(rows)) return true;
        return computeTotals(rows).difference === 0;
      })
      /**
       * At least two different accounts. A voucher whose lines all point at one
       * account moves nothing — it only nets that account against itself. 162
       * production vouchers across 21 associations do this (measured 2.9.2026),
       * mostly from using the form as a single-entry cash book with no
       * counter-entry.
       *
       * This is the rule that catches Honkataipale's voucher 9550: a 9,40 €
       * bank charge entered as debet and kredit on account 650, which balances
       * perfectly, nets to zero, and never touches the bank.
       */
      .test("distinct-accounts", "Valitse myös vastatili", (transactions) => {
        const rows = (transactions ?? []) as TransactionFormRow[];
        if (!rowAmountsUsable(rows)) return true;
        if (rows.length < 2) return true; // the min(1)/row rules speak first
        const codes = new Set(
          rows.map((tx) => tx?.accountCode).filter((c) => c !== "" && c != null)
        );
        return codes.size >= 2;
      }),
  });
};

/** Form rows carry strings; the API column is numeric. */
export const toTransactionPayload = (transactions: TransactionFormRow[]) =>
  (transactions ?? []).map((tx) => ({
    ...tx,
    debit: parseAmt(tx.debit),
    credit: parseAmt(tx.credit),
  }));
